import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { UserAgent, Inviter, Registerer, SessionState, RegistererState } from "sip.js";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Convert any phone format to a Sipgate-friendly dial string.
 * Sipgate accepts German numbers best in domestic format (0163...).
 * International non-German numbers get a 00-prefix.
 */
function normalizeForSipgate(raw) {
  if (!raw) return "";
  let s = String(raw).replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s.startsWith("+49")) return "0" + s.slice(3);          // +491638... -> 01638...
  if (s.startsWith("0049")) return "0" + s.slice(4);         // 00491638... -> 01638...
  if (s.startsWith("+")) return "00" + s.slice(1);           // +1555...  -> 001555...
  if (s.startsWith("00")) return s;                          // 001555... -> 001555...
  if (s.startsWith("0")) return s;                           // 01638...  -> 01638...
  return s;
}

const SipPhoneContext = createContext(null);

export function useSipPhone() {
  const ctx = useContext(SipPhoneContext);
  if (!ctx) {
    // Safe no-op fallback when used outside the provider (e.g. login page)
    return {
      registered: false, registering: false, error: null, credentials: null,
      callState: "idle", currentNumber: null, muted: false,
      callStartedAt: null, callConnectedAt: null,
      connect: () => {}, call: () => null, hangup: () => {},
      toggleMute: () => {}, sendDtmf: () => {},
    };
  }
  return ctx;
}

/** Keep a single shared audio element for outgoing/incoming streams */
function ensureAudioElement() {
  let el = document.getElementById("sip-remote-audio");
  if (!el) {
    el = document.createElement("audio");
    el.id = "sip-remote-audio";
    el.autoplay = true;
    document.body.appendChild(el);
  }
  return el;
}

export function SipPhoneProvider({ children }) {
  const { user } = useAuth();
  const [registered, setRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState(null);
  const [callState, setCallState] = useState("idle");          // idle | calling | ringing | connected | ended
  // ── Frühzeitige Termination-Erkennung ─────────────────────────────────
  // Sipgate (oder das Gateway) terminiert oft Anrufe VOR der Verbindung mit
  // Codes wie 486 (Busy), 487 (Cancelled), 503 (Service Unavailable),
  // 408 (Request Timeout) oder bei ICE-Failure. Im SIP.js-State-Machine
  // springt es dann direkt von Establishing → Terminated (überspringt
  // Established). Ohne diesen Flag würde der Logging-Step die Ursache
  // nicht kennen → User sieht Ergebnis-Feld obwohl er nie verbunden war.
  // Werte: 'success' (verbunden+aufgelegt) | 'rejected' (Gegenseite besetzt/abgelehnt)
  //        | 'no_answer' (Klingelte zu lange ohne Annahme/Timeout)
  //        | 'failed' (Netzwerk/SIP-Fehler vor Verbindung) | null (idle/aktiv)
  const [lastCallResult, setLastCallResult] = useState(null);
  const [currentNumber, setCurrentNumber] = useState(null);
  const [muted, setMuted] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [callConnectedAt, setCallConnectedAt] = useState(null);
  const [credentials, setCredentials] = useState(null);

  const uaRef = useRef(null);
  const registererRef = useRef(null);
  const sessionRef = useRef(null);
  const triedAutoConnectRef = useRef(false);

  // Ringback (Klingel-Tuten) Web-Audio-Refs — generiert clientseitig den DE-Ton
  // (425 Hz, 1s an / 4s aus), da Sipgate Early-Media via WebRTC nicht zuverlässig durchreicht.
  const ringbackCtxRef = useRef(null);
  const ringbackTimerRef = useRef(null);

  const startRingback = useCallback(() => {
    if (ringbackCtxRef.current) return; // läuft schon
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      // Manche Browser starten AudioContext "suspended" — sicherheitshalber resume()
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      ringbackCtxRef.current = ctx;
      const beep = () => {
        const c = ringbackCtxRef.current;
        if (!c) return;
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = "sine";
        osc.frequency.value = 425; // CCITT/DE Ringback-Frequenz
        const t = c.currentTime;
        // sanfter Ein-/Ausblendgain damit es kein Knack gibt
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.18, t + 0.05);
        gain.gain.setValueAtTime(0.18, t + 0.95);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.0);
        osc.connect(gain);
        gain.connect(c.destination);
        osc.start(t);
        osc.stop(t + 1.05);
      };
      beep(); // sofort 1. Ton
      // DE-Pattern: 1 Sekunde Ton + 4 Sekunden Pause = alle 5s wiederholen
      ringbackTimerRef.current = setInterval(beep, 5000);
    } catch (e) {
      console.warn("Ringback start failed:", e);
    }
  }, []);

  const stopRingback = useCallback(() => {
    if (ringbackTimerRef.current) {
      clearInterval(ringbackTimerRef.current);
      ringbackTimerRef.current = null;
    }
    if (ringbackCtxRef.current) {
      try { ringbackCtxRef.current.close(); } catch (_) { /* noop */ }
      ringbackCtxRef.current = null;
    }
  }, []);

  /** Connect: fetch credentials and register at Sipgate WebSocket SIP server */
  const connect = useCallback(async () => {
    if (uaRef.current || registering) return;
    setRegistering(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/integrations/sipgate/sip-credentials`, { withCredentials: true });
      const c = res.data;
      setCredentials(c);

      const uri = UserAgent.makeURI(`sip:${c.username}@${c.sip_server}`);
      if (!uri) throw new Error("Ungültige SIP-URI");

      const userAgent = new UserAgent({
        uri,
        authorizationUsername: c.username,
        authorizationPassword: c.password,
        transportOptions: { server: c.websocket_url },
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: [
              { urls: "stun:stun.sipgate.net:3478" },
              { urls: "stun:stun.l.google.com:19302" },
            ],
          },
        },
        // Auto-accept incoming sessions (we'll wire UI later)
        delegate: {
          onInvite(invitation) {
            sessionRef.current = invitation;
            setCallState("ringing");
            setCurrentNumber(invitation.remoteIdentity?.uri?.user || "Unbekannt");
            setupSessionListeners(invitation);
          },
        },
      });

      await userAgent.start();
      uaRef.current = userAgent;

      const registerer = new Registerer(userAgent);
      registerer.stateChange.addListener((state) => {
        if (state === RegistererState.Registered) {
          setRegistered(true);
        } else if (state === RegistererState.Unregistered || state === RegistererState.Terminated) {
          setRegistered(false);
        }
      });
      await registerer.register();
      registererRef.current = registerer;
    } catch (e) {
      console.error("SIP connect failed:", e);
      const msg = e?.response?.data?.detail || e.message || "SIP-Verbindung fehlgeschlagen";
      const isAuthError = e?.response?.status === 401;
      setError(msg);
      toast.error(isAuthError
        ? "Sipgate-Verbindung abgelaufen — bitte unter Einstellungen → Integrationen neu verbinden"
        : `SIP-Verbindung fehlgeschlagen: ${msg}`
      );
    } finally {
      setRegistering(false);
    }
  }, [registering]);

  /** Wire up audio + state changes on a session */
  const setupSessionListeners = (session) => {
    // Track ob Session jemals durch `Established` gegangen ist — entscheidet
    // bei `Terminated` ob es ein normaler BYE oder eine frühe Abbruch-
    // Ursache war.
    let everEstablished = false;
    let lastResponseStatus = null;
    let everRinging = false;

    // Capture response codes via inviter delegate (only for outgoing).
    // SIP.js liefert den Response-Status via Inviter.delegate.onResponse.
    if (session.delegate || session.delegate === undefined) {
      session.delegate = session.delegate || {};
      session.delegate.onResponse = (response) => {
        const code = response?.message?.statusCode;
        if (typeof code === "number") {
          lastResponseStatus = code;
          // 180 Ringing / 183 Session Progress → Gegenseite klingelt nun
          if (code === 180 || code === 183) {
            everRinging = true;
            setCallState("ringing");
          }
        }
      };
    }

    session.stateChange.addListener((state) => {
      switch (state) {
        case SessionState.Establishing:
          setCallState("calling");
          startRingback();
          break;
        case SessionState.Established: {
          everEstablished = true;
          stopRingback();
          setCallState("connected");
          setCallConnectedAt(Date.now());
          // Hook up remote audio
          const sdh = session.sessionDescriptionHandler;
          const peer = sdh?.peerConnection;
          if (peer) {
            const remoteStream = new MediaStream();
            peer.getReceivers().forEach((r) => {
              if (r.track) remoteStream.addTrack(r.track);
            });
            ensureAudioElement().srcObject = remoteStream;
          }
          break;
        }
        case SessionState.Terminated: {
          stopRingback();
          // ── Klassifiziere Termination-Ursache ──────────────────────────
          // SIP-Standard-Codes:
          //   486 Busy Here, 600 Busy Everywhere → rejected
          //   487 Request Terminated (oft Cancelled vom Caller)
          //   408 Request Timeout, 480 Temporarily Unavailable → no_answer
          //   503/500/5xx Server Error, 403 Forbidden → failed
          //   404 Not Found (Nummer existiert nicht) → failed
          //   200 OK → success (sollte everEstablished=true triggern)
          let result;
          if (everEstablished) {
            result = "success";
          } else if (lastResponseStatus === 486 || lastResponseStatus === 600 ||
                     lastResponseStatus === 603 /* decline */) {
            result = "rejected";
            toast.error("Gegenseite ist besetzt oder hat abgelehnt");
          } else if (lastResponseStatus === 408 || lastResponseStatus === 480 ||
                     (everRinging && !everEstablished)) {
            result = "no_answer";
            toast.warning("Keine Antwort — der Anruf wurde nicht angenommen");
          } else if (lastResponseStatus === 487) {
            // Request Terminated — kann selbst-cancel ODER server-cancel sein
            result = everRinging ? "no_answer" : "failed";
            if (!everRinging) {
              toast.error("Anruf abgebrochen (Code 487 — meist Sipgate-Routing-Problem)");
            }
          } else if (lastResponseStatus && lastResponseStatus >= 400) {
            result = "failed";
            const codeLabel = {
              403: "Sipgate hat den Anruf blockiert (Code 403 — keine Berechtigung)",
              404: "Nummer nicht erreichbar (Code 404)",
              503: "Sipgate-Gateway temporär überlastet (Code 503)",
            }[lastResponseStatus];
            toast.error(codeLabel || `Anruf fehlgeschlagen (SIP-Code ${lastResponseStatus})`);
          } else {
            // Kein Response-Code → meist ICE-Failure / Netzwerk-Reset
            result = "failed";
            toast.error("Anruf-Verbindung fehlgeschlagen — bitte erneut versuchen");
          }
          setLastCallResult(result);
          setCallState("ended");
          setTimeout(() => {
            setCallState("idle");
            setCurrentNumber(null);
            setCallStartedAt(null);
            setCallConnectedAt(null);
            setMuted(false);
            sessionRef.current = null;
            // lastCallResult NICHT zurücksetzen — der CallTrackerDialog
            // braucht den Wert um beim Logging die richtige Outcome zu wählen.
            // Wird beim nächsten `call()` überschrieben.
          }, 1500);
          break;
        }
        default:
          break;
      }
    });
  };

  /** Outgoing call */
  const call = useCallback(async (phoneNumber) => {
    if (!uaRef.current || !registered) {
      toast.error("SIP nicht verbunden – bitte zuerst Telefon verbinden.");
      return null;
    }
    if (sessionRef.current) {
      toast.error("Es läuft bereits ein Anruf.");
      return null;
    }

    const dialNumber = normalizeForSipgate(phoneNumber);
    if (!dialNumber) {
      toast.error("Ungültige Zielnummer");
      return null;
    }
    const target = UserAgent.makeURI(`sip:${dialNumber}@${credentials?.sip_server || "sipgate.de"}`);
    if (!target) {
      toast.error("Ungültige Zielnummer");
      return null;
    }

    setCurrentNumber(phoneNumber);
    setCallStartedAt(Date.now());
    setLastCallResult(null);  // Reset für neuen Anruf
    setCallState("calling");
    startRingback(); // schon vor invite() starten — sofort akustisches Feedback

    const inviter = new Inviter(uaRef.current, target, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });
    setupSessionListeners(inviter);
    sessionRef.current = inviter;

    try {
      await inviter.invite();
      return inviter;
    } catch (e) {
      console.error("Call failed:", e);
      stopRingback();
      toast.error(`Anruf fehlgeschlagen: ${e.message || ""}`);
      setCallState("idle");
      sessionRef.current = null;
      return null;
    }
  }, [registered, credentials, startRingback, stopRingback]);

  const hangup = useCallback(async () => {
    const s = sessionRef.current;
    stopRingback();
    if (!s) return;
    try {
      switch (s.state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          if (s.cancel) await s.cancel();
          else if (s.reject) await s.reject();
          break;
        case SessionState.Established:
          await s.bye();
          break;
        default:
          break;
      }
    } catch (e) {
      console.error("Hangup error:", e);
    }
  }, [stopRingback]);

  const toggleMute = useCallback(() => {
    const s = sessionRef.current;
    if (!s || s.state !== SessionState.Established) return;
    const peer = s.sessionDescriptionHandler?.peerConnection;
    if (!peer) return;
    const senders = peer.getSenders();
    senders.forEach((sender) => {
      if (sender.track && sender.track.kind === "audio") {
        sender.track.enabled = !sender.track.enabled;
      }
    });
    setMuted((m) => !m);
  }, []);

  const sendDtmf = useCallback((digit) => {
    const s = sessionRef.current;
    if (!s || s.state !== SessionState.Established) return;
    try {
      s.sessionDescriptionHandler?.sendDtmf?.(digit);
    } catch (e) { /* ignore */ }
  }, []);

  /** Cleanup on unmount */
  useEffect(() => {
    return () => {
      stopRingback();
      try { registererRef.current?.unregister(); } catch (e) {}
      try { uaRef.current?.stop(); } catch (e) {}
    };
  }, [stopRingback]);

  /** Auto-connect when user logs in (once per session). Disconnect on logout. */
  useEffect(() => {
    if (user && !triedAutoConnectRef.current && !uaRef.current && !registering) {
      triedAutoConnectRef.current = true;
      // Slight delay so the rest of the app finishes rendering & the user is fully authenticated
      const t = setTimeout(() => { connect(); }, 1500);
      return () => clearTimeout(t);
    }
    if (!user && uaRef.current) {
      // User logged out – clean up
      stopRingback();
      try { registererRef.current?.unregister(); } catch (e) {}
      try { uaRef.current?.stop(); } catch (e) {}
      uaRef.current = null;
      registererRef.current = null;
      triedAutoConnectRef.current = false;
      setRegistered(false);
      setCredentials(null);
      setError(null);
    }
  }, [user, connect, registering, stopRingback]);

  const value = {
    registered, registering, error, credentials,
    callState, currentNumber, muted,
    callStartedAt, callConnectedAt,
    lastCallResult,
    connect, call, hangup, toggleMute, sendDtmf,
  };

  return <SipPhoneContext.Provider value={value}>{children}</SipPhoneContext.Provider>;
}
